import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import 'react-dropdown-tree-select/dist/styles.css';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Button, Row, Col } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { isAdmin } from '../../redux/MiscSlice';
import {
    AccountRootDependencyInput, AccountDependencyInput, AccountDependencySetInput, ConceptInput, ConceptLabelInput,
    EditDeprecation, getTaxonomyChanges, ReferenceAssignmentChange, RelationshipInput,
    removeSelectedChanges, TaxonomyChangesState
} from '../../redux/TaxonomyChangesSlice';
import { CustomReferenceRole, getPartValue, getShortTypeNameString, getUasId, Part, PartDefinition, Reference } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion } from '../../redux/TaxonomyVersionSlice';
import enums from '../../utils/enums';


interface ChangeTableData {
    changeId: string,
    change: any | null;
    type: string;
    affects: string;
    summary: string | null;
    _children: ChangeTableData[] | null;
}
const ChangeManager = () => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var changes: TaxonomyChangesState | null = useAppSelector(getTaxonomyChanges);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var changeTabulatorRef = useRef<any>(null);

    const addGroupRow = function (masterList: ChangeTableData[], groupType: string, childList: ChangeTableData[]) {
        var masterRow: ChangeTableData = {
            changeId: groupType, change: null, type: groupType, affects: "", summary: "", _children: (childList.length === 0) ? null : childList
        }
        masterList.push(masterRow);
    }
    const createReferenceChangeRow = function (chg: Reference, index: number): ChangeTableData {
        return {
            changeId: enums.changeTrackingPrefix.REFCHANGE + index, change: chg, type: "Reference Change", affects: chg.type,
            summary: chg.parts.map((p: Part) => p.definition.name + ": " + p.value).join("\n"),
            _children: null
        }
    }
    const createReferenceAssignmentChangeRow = function (chg: ReferenceAssignmentChange, index: number): ChangeTableData {
        var typeInfo = getShortTypeNameString(chg.reference.type);
        var summaryInfo = getUasId(chg.reference, null);
        if (summaryInfo.length === 0) {
            summaryInfo = getPartValue(chg.reference, "Name");
        }
        return { changeId: enums.changeTrackingPrefix.REFASSIGNCHANGE + index, change: chg, type: typeInfo, affects: chg.concept.id, summary: summaryInfo, _children: null }
    }
    const createConceptLabelChangeRow = function (chg: ConceptLabelInput, index: number): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.LABELCHANGE + index, change: chg, type: chg.role, affects: chg.conceptId, summary: chg.value, _children: null }
    }
    const createConceptChangeRow = function (chg: ConceptInput, index: number): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.CONCEPTCHANGE + index, change: chg, type: "Concept", affects: chg.id, summary: "", _children: null }
    }
    const createPartDefinitionChangeRow = function (chg: PartDefinition, index: number): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.PARTDEFINITIONCHANGE + index, change: chg, type: chg.group, affects: chg.name, summary: chg.datatype + ": " + chg.documentation, _children: null }
    }
    const createCustomRoleChangeRow = function (chg: CustomReferenceRole, index: number): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.CUSTOMROLECHANGE + index, change: chg, type: "Custom Role", affects: chg.name, summary: chg.documentation, _children: null }
    }
    const createDeprecationRow = function (chg: EditDeprecation, index: number): ChangeTableData {
        var summary = ["date: " + chg.deprecationDate, "reason: " + chg.reason];
        if (chg.replacements && chg.replacements.length > 0) {
            summary.push("Replacements:")
            chg.replacements.map(rep => summary.push(rep.conceptName));
        }
        return { changeId: enums.changeTrackingPrefix.DEPRECATIONCHANGE + index, change: chg, type: "Deprecation", affects: chg.conceptId, summary: summary.join("\n"), _children: null }
    }
    const createRelationshipChangeRow = function (chg: RelationshipInput, index: number): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.RELATONSHIPCHANGE + index, change: chg, type: "Relationship", affects: chg.fromId, summary: "", _children: null }
    }
    const createEntityScopeChangeRow = function (scopeIds: string[], key: string): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.ENTITYSCOPECHANGE + key, change: scopeIds, type: "Entity Scope", affects: key, summary: scopeIds ? scopeIds.join("\n") : "", _children: null }
    }
    const createDimensionalScopeChangeRow = function (scopeIds: string[], key: string): ChangeTableData {
        return { changeId: enums.changeTrackingPrefix.DIMENSIONALSCOPECHANGE + key, change: scopeIds, type: "Dimensional Scope", affects: key, summary: scopeIds ? scopeIds.join("\n") : "", _children: null }
    }

    const accountDependencyReference = function (dep: AccountRootDependencyInput) {
        var depSummaryName: string | null = dep.name ? dep.name : "";
        if (dep.newName) {
            depSummaryName += " (" + dep.newName + ")";
        }
        if (dep.relationship) {
            switch (dep.relationship) {
                case enums.dependencyRelationship.EQUALS:
                case enums.dependencyRelationship.GREATER_THAN:
                case enums.dependencyRelationship.GREATER_THAN_EQUAL:
                case enums.dependencyRelationship.LESS_THAN:
                case enums.dependencyRelationship.LESS_THAN_EQUAL:
                    depSummaryName += " " + enums.dependencyRelationshipMapping[dep.relationship] + " " + dep.value;
                    break;

                case enums.dependencyRelationship.TEST:
                    depSummaryName += " ={ " + dep.test + " }";
                    break;

                default:
                    if (dep.recursiveDependencies) {
                        depSummaryName += " " + dep.relationship + " deps(" + dep.recursiveDependencies.length + ") ";
                    }
                    break;
            }
        }

        return depSummaryName;
    }
    const createAccountDependencyChangeRow = function (chg: AccountDependencySetInput, key: string): ChangeTableData {
        var summaryInfo = chg.dependencies.map((dep: AccountRootDependencyInput) => accountDependencyReference(dep));
        return { changeId: enums.changeTrackingPrefix.DEPENDENCYCHANGE + key, change: chg, type: "Account Dependency", affects: chg.accountId, summary: summaryInfo.join("\n"), _children: null }
    }
    const generateChangeTableData = function (chgState: TaxonomyChangesState | null) {
        var result: ChangeTableData[] = [];
        if (chgState != null) {
            addGroupRow(result, "Reference changes", chgState.referenceChanges.map((cc, index) => createReferenceChangeRow(cc, index)));
            addGroupRow(result, "Reference assignment changes", chgState.referenceAssignmentChanges.map((cc, index) => createReferenceAssignmentChangeRow(cc, index)));
            addGroupRow(result, "Concept Label changes", chgState.conceptLabelChanges.map((cc, index) => createConceptLabelChangeRow(cc, index)));
            addGroupRow(result, "Concept changes", chgState.conceptChanges.map((cc, index) => createConceptChangeRow(cc, index)));
            addGroupRow(result, "Part Definition changes", chgState.partDefinitionChanges.map((cc, index) => createPartDefinitionChangeRow(cc, index)));
            addGroupRow(result, "Custom Role changes", chgState.customRoleChanges.map((cc, index) => createCustomRoleChangeRow(cc, index)));
            addGroupRow(result, "Deprecations", chgState.deprecations.map((cc, index) => createDeprecationRow(cc, index)));
            addGroupRow(result, "Relationship changes", chgState.relationshipChanges.map((cc, index) => createRelationshipChangeRow(cc, index)));
            addGroupRow(result, "Entity Scope changes", Object.keys(chgState.entityScopeChanges).map(key => createEntityScopeChangeRow(chgState.entityScopeChanges[key], key)));
            addGroupRow(result, "Dimensional Scope changes", Object.keys(chgState.dimensionalScopeChanges).map(key => createDimensionalScopeChangeRow(chgState.dimensionalScopeChanges[key], key)));
            addGroupRow(result, "Account Dependency changes", Object.keys(chgState.accountDependencyChanges).map(key => createAccountDependencyChangeRow(chgState.accountDependencyChanges[key], key)));
        }
        return result;
    }
    const [tableData, setTableData] = useState<ChangeTableData[] | null>([]);

    const handleUndoClick = function (event: any) {
        if (!changeTabulatorRef.current || !changeTabulatorRef.current.table) {
            return;
        }
        var selections = changeTabulatorRef.current.table.getSelectedData();
        if (selections && selections.length > 0) {
            dispatch(removeSelectedChanges(selections.map((sel: any) => sel.changeId)));
        }
    }
    const handleDeleteCellClick = function (event: any, cell: any) {
        var row: any = cell.getRow();
        row.getTable().deselectRow();
        row.select();
        event.stopPropagation();
    }

    const handleRowClick = function (event: any, row: any) {
    }

    const handleTickBoxClick = function (e: any, cell: any) {
        cell.getRow().toggleSelect();
    }

    // handle the logic to add a new account 
    const addAccountTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

        //build dropdown
        var button = document.createElement("div");

        button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Account...'></i>  Account";

        button.onclick = function () {
            // add code here

        }

        return button;
    }

    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }

    const newGridOptions = {
        rowClick: (e: any, cell: any) => handleRowClick(e, cell),
        selectable: true,
        dataTree: true
    };

    const defaultColumns = [
        { title: "Type", field: "type", width: 280 },
        { title: "Identifier/Affects", field: "affects", width: 300 },
        { title: "Summary", field: "summary", formatter: "textarea" },
        { formatter: "rowSelection", titleFormatter: "rowSelection", width: 40, hozAlign: "center", headerSort: false, cellClick: handleTickBoxClick }
    ];

    useEffect(() => {
        setTableData(generateChangeTableData(changes));
    }, [versionInfo, changes]);

    React.useEffect(() => {

        changeTabulatorRef.current = new Tabulator('#changeManager', {
            data: tableData,
            columns: defaultColumns,
            layout: "fitColumns",
            selectable: true,
            dataTree: true

        }).on("rowClick", handleRowClick);

    }, [, tableData])

    return (
        <React.Fragment>
            <Row>
                <Col>
                    <div style={flexRightChild} >
                        <Button type="button" className="taxedit" disabled={false} onClick={handleUndoClick}>Undo Selected Changes</Button>
                    </div>
                    <div id="changeManager"
                        ref={ele => { changeTabulatorRef.current = ele }} />
                </Col>
            </Row>
        </React.Fragment>
    );
}
export default ChangeManager;